import {
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Circle,
  DirectionsRenderer,
  DirectionsService,
  DrawingManager,
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import "./InfoWindow.css";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";

const DynamicIconRender = lazy(() => import("../IconPicker/DynamicIconRender"));

const containerStyle = {
  width: "100%",
  height: "100%",
};

const MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID;

// Things about this component:
// 1. Renders markers on the map
// 2. Renders popUpContent when a marker is clicked
// 3. Centers the map when a marker is clicked
// 4. Renders icons on the map
// 5. Animates the map when the user navigates to different points
// 6. This is a controlled component. So whatever invokes this will need to set
// the default location / first item.
//7. Renders a 3d model if available and specified

export default function GoogleMapComponent(props) {
  // Event Types map is required if you want to render icons.
  const {
    activeMarker = null,
    // Additional map items are things like drawing tools, or other things that you want to render on the map
    additionalMapItems = null,

    circlesToRender = [],

    // This is used by routing, where is the user going
    destination,

    // Drawing Control allows a user to draw on the map
    drawingControl = false,

    // If facility markers are rendered on the map, this is the content that will be displayed when a facility marker is clicked.
    facilityPopUpContent = null,

    // This is the function that is called when a marker is clicked. It returns the marker object
    handleMarkerClick = null,

    highlightedZone = null,

    eventTypesMap = {},

    // We render facilities on the map. This is a list of facilities that are rendered on the map.
    facilities = [],
    floorPlanData = {},

    mapSettings = {
      center: { lat: 60.363007, lng: -43.6339 },
      mapTypeId: "satellite",
      zoom: 5,
    },
    markersToRender = [],

    // Marker key is what is used as a key when we iterate through the given markers
    markerKey = "assetHistoryId",

    // onPolygonComplete is a function that is called when a user is done drawing a polygon. It returns a array with a polygon object
    onPolygonComplete = () => { },

    onCircleClick = () => { },

    options = {},

    // This is used by routing, where is the user starting
    origin,

    // popUpContent is what appears when a marker is clicked.
    popUpContent = null,

    // popUpZoneContent is what appears when a zone (Circle) is clicked.
    popUpZoneContent = null,

    // Polylines show 'trips' devices take instead of a line of markers
    polylines = [],

    // Polygons
    polygons = [],

    // polylinePopUpContent is what appears when a polyline is clicked.
    polylinePopUpContent = null,
    setActiveMarker = () => { },
    setMapSettings = () => { },

    // This is used by routing, how is the user getting there
    travelMode = "WALKING",

    // This is used to control when the zone info window is open
    onCircleInfoWindowClose = () => { },

  } = props;

  const {
    center = { lat: 60.363007, lng: -43.6339 },
    mapTypeId = "satellite",
    zoom = 5,
  } = mapSettings;

  const mapRef = useRef();
  const overlayRef = useRef();
  const modelRef = useRef();
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const [mapInstance, setMapInstance] = useState(null);

  const [isLoaded, setIsLoaded] = useState(true);
  const drawingManagerRef = useRef(null);
  const [state, setState] = useState({
    showInfoWindow: false,
    infoWindowContent: null,
    infoWindowLocation: {},
  });
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [requestDirections, setRequestDirections] = useState(false); // Control when to request directions
  const [isRouteRendered, setIsRouteRendered] = useState(false); // Track if the route has been rendered

  // Dotted Line Path is used to show a line from the last navigable point to the destination
  const [dottedLinePath, setDottedLinePath] = useState([]); // State to store the dotted line path

  const { infoWindowContent, showInfoWindow, infoWindowLocation } = state;

  // We require two seperate info windows for markers and polylines.
  // The info window that sits on the marker is styled to sit directly ontop of the marker.
  // The UI is thrown off when we abstract this away from the marker component
  const [polylineInfoWindowState, setPolylineInfoWindowState] = useState({
    isOpen: false,
    position: null,
  });

  const onLoad = useCallback(function callback(map) {
    mapRef.current = map;
    setMapInstance(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    mapRef.current = null;
  }, []);

  const handlePolygonComplete = (polygon) => {
    const path = polygon
      .getPath()
      .getArray()
      .map((latLng) => ({
        lat: latLng.lat(),
        lng: latLng.lng(),
      }));
    const fillColor = "#008000";

    // setPolygonPaths([...polygonPaths, { path, fillColor }]);
    onPolygonComplete({ path, fillColor });
  };

  const handleDirectionsCallback = (response) => {
    if (response !== null && response.status === "OK") {
      setDirectionsResponse(response); // Save the response
      setRequestDirections(false); // Stop requesting directions after a successful response

      // Check if the destination is not reachable by road
      const route = response.routes[0];
      const lastLeg = route.legs[route.legs.length - 1];
      const lastPoint = lastLeg.steps[lastLeg.steps.length - 1].end_location;

      // Validate destination
      const destinationPoint =
        typeof destination === "string"
          ? {
            lat: parseFloat(destination.split(",")[0].trim()),
            lng: parseFloat(destination.split(",")[1].trim()),
          }
          : destination &&
            destination.lat !== undefined &&
            destination.lng !== undefined
            ? { lat: destination.lat, lng: destination.lng }
            : null;

      // If the last point is not the exact destination, add a dotted line
      if (
        destinationPoint &&
        (lastPoint.lat() !== destinationPoint.lat ||
          lastPoint.lng() !== destinationPoint.lng)
      ) {
        setDottedLinePath([
          { lat: lastPoint.lat(), lng: lastPoint.lng() }, // Last navigable point
          destinationPoint, // Exact destination
        ]);
      } else {
        setDottedLinePath([]); // Clear the dotted line if the destination is reachable
      }
    } else {
      setRequestDirections(false); // Stop requesting directions even if it fails
    }
  };

  // This useEffect is to animate the map as the user navigates to different points
  useEffect(() => {
    if (
      mapRef.current?.panTo &&
      mapSettings.center &&
      mapSettings.center.lat !== undefined &&
      mapSettings.center.lng !== undefined
    )
      mapRef.current?.panTo(mapSettings.center);
  }, [mapSettings.center]);

  // Clear the route when destination is set to null
  useEffect(() => {
    if (!destination) {
      setDirectionsResponse(null); // Clear the directions response
      setIsRouteRendered(false); // Reset the route rendered state
    }
  }, [destination]);

  // Trigger DirectionsService only when origin or destination changes
  useEffect(() => {
    if (origin && destination) {
      setRequestDirections(true); // Enable DirectionsService rendering
    }
  }, [origin, destination]);

  // This useEffect listens to whether the destination or origin is set to null. If so, we clear the dottedLinePath
  useEffect(() => {
    if (!destination || !origin) {
      setDottedLinePath([]); // Reset the dotted line path to its initial state
    }
  }, [destination, origin]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || overlayRef.current) return;

    const overlay = new window.google.maps.WebGLOverlayView();
    overlayRef.current = overlay;

    overlay.onAdd = () => {
      const scene = new THREE.Scene();
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera();
      cameraRef.current = camera;

      const light = new THREE.AmbientLight(0xffffff, 0.75);
      scene.add(light);

      const dirLight = new THREE.DirectionalLight(0xffffff, 0.75);
      dirLight.position.set(0, 10, 0).normalize();
      scene.add(dirLight);

      const loader = new GLTFLoader();
      loader.load(floorPlanData.glbUrl ? floorPlanData?.glbUrl : "/house_floor_plan.glb", (gltf) => {
        const model = gltf.scene;

        model.scale.set(
          floorPlanData?.scale,
          floorPlanData?.scale,
          floorPlanData?.scale
        );

        model.rotateX(floorPlanData?.rotation?.x || 0); // Rotate around X-axis
        model.rotateY(floorPlanData?.rotation?.y || 0); // Rotate around Y-axis
        model.rotateZ(floorPlanData?.rotation?.z || 0); // Rotate around Z-axis

        modelRef.current = model;
        scene.add(model);
      });
    };

    overlay.onContextRestored = ({ gl }) => {
      const renderer = new THREE.WebGLRenderer({
        canvas: gl.canvas,
        context: gl,
        alpha: true,
      });
      renderer.autoClear = false;
      rendererRef.current = renderer;
    };

    overlay.onDraw = ({ gl, transformer }) => {
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      const model = modelRef.current;

      if (!scene || !camera || !renderer || !model) return;

      const matrix = transformer.fromLatLngAltitude({
        lat: floorPlanData?.center?.lat || center.lat,
        lng: floorPlanData?.center?.lng || center.lng,
        altitude: 0,
      });

      camera.projectionMatrix.fromArray(matrix);

      renderer.clearDepth();
      renderer.render(scene, camera);
      renderer.resetState();
    };

    overlay.setMap(mapRef.current);
  }, [isLoaded, center, floorPlanData]);

  useEffect(() => {
    if (!mapInstance) return;
    const container = mapInstance.getDiv();

    let startX, startY, startTilt, startHeading;

    const onMouseDown = (e) => {
      if (e.button !== 2) return; // right‐button only
      e.preventDefault();
      startX = e.clientX;
      startY = e.clientY;
      startTilt = mapInstance.getTilt() || 0;
      startHeading = mapInstance.getHeading() || 0;
      container.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      if (!(e.buttons & 2)) {
        container.style.cursor = '';
        return;
      }

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const newHeading = startHeading + dx * 0.5;
      let newTilt = startTilt + dy * 0.5;
      newTilt = Math.max(0, Math.min(newTilt, mapInstance.maxTilt || 67.5));

      mapInstance.moveCamera({
        heading: newHeading,
        tilt: newTilt,
        center: mapInstance.getCenter(),
        zoom: mapInstance.getZoom(),
      });
    };

    const onMouseUp = (e) => {
      if (e.button !== 2) return;
      container.style.cursor = '';
    };

    const onContextMenu = (e) => {
      e.preventDefault();
    };

    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    container.addEventListener('contextmenu', onContextMenu);

    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('contextmenu', onContextMenu);
    };
  }, [mapInstance]);


  return isLoaded ? (
    <>
      <GoogleMap
        center={center}
        mapContainerStyle={containerStyle}
        onDragEnd={() => {
          const center = mapRef.current.getCenter().toJSON();
          const newZoom = mapRef.current.getZoom();

          setMapSettings((prevState) => ({
            ...prevState,
            center: { lat: center.lat, lng: center.lng },
            zoom: newZoom,
            mapTypeId: mapTypeId, // Preserve the current mapTypeId
          }));
        }}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onMapTypeIdChanged={() => {
          const newMapTypeId = mapRef?.current?.getMapTypeId(); // Get the current mapTypeId

          // Only update the state if the mapTypeId has changed
          if (newMapTypeId !== mapSettings.mapTypeId) {
            setMapSettings((prevState) => ({
              ...prevState,
              mapTypeId: newMapTypeId, // Update the mapTypeId in state
            }));
          }
        }}
        onZoomChanged={() => {
          const newZoom = mapRef?.current?.getZoom();

          setMapSettings((prevState) => ({
            ...prevState,
            zoom: newZoom ? newZoom : prevState.zoom,
          }));

          // Close the info window if clustering is likely to occur
          if (newZoom < 16) {
            // Adjust the zoom level threshold as needed
            setActiveMarker(null); // Close the info window
          }
        }}
        options={{
          ...options,
          draggable: true, // Allow dragging
          gestureHandling: 'greedy', // enables full gesture control
          mapId: MAP_ID,
          mapTypeId: mapTypeId, // Use the current mapTypeId
          rotateControl: true, // this allows rotation UI
          scrollwheel: true, // Allow zooming with the mouse wheel
          zoomControl: true,
        }}
        yesIWantToUseGoogleMapApiInternals
        style={{
          height: "100%", // Ensure the map container has proper height
          width: "100%", // Ensure the map container has proper width
        }}
        zoom={zoom}
      >
        {/* General Info window */}
        {showInfoWindow && (
          <InfoWindow
            position={infoWindowLocation}
            onCloseClick={() =>
              setState({ showInfoWindow: false, infoWindowLocation: {} })
            }
          >
            {infoWindowContent}
          </InfoWindow>
        )}

        {/* Zones Pop-Up */}
        {highlightedZone && (() => {
          const zoneObject = circlesToRender.find(
            (circle) => circle.zoneId === highlightedZone
          );
          if (!zoneObject) return null;
          return (
            <InfoWindow
              position={{
                lat: zoneObject.xyz?.x || 0,
                lng: zoneObject.xyz?.y || 0,
              }}
              onCloseClick={() => onCircleInfoWindowClose()}
            >
              {popUpZoneContent ? popUpZoneContent(zoneObject) : null}
            </InfoWindow>
          );
        })()}

        {/* Info window for Polylines */}
        {polylineInfoWindowState.isOpen ? (
          <InfoWindow
            position={polylineInfoWindowState.position}
            onCloseClick={() =>
              setPolylineInfoWindowState({ isOpen: false, position: null })
            }
          >
            {polylinePopUpContent(polylineInfoWindowState)}
          </InfoWindow>
        ) : null}

        {/* Markers */}
        <MarkerClusterer options={{ maxZoom: 15 }}>
          {(clusterer) =>
            markersToRender.length > 0
              ? markersToRender.map((marker) => {
                const { event = "", latitude = 0, longitude = 0 } = marker;

                const uniqueIdentifierUUID = marker[markerKey];

                return eventTypesMap[event] && eventTypesMap[event].icon ? (
                  <OverlayView
                    key={uniqueIdentifierUUID}
                    position={{ lat: latitude, lng: longitude }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    {/* <Suspense> */}
                    <div
                      onClick={() => setActiveMarker(uniqueIdentifierUUID)}
                    >
                      <DynamicIconRender
                        iconName={eventTypesMap[event]?.icon || "LocationOn"}
                      />
                    </div>
                    {activeMarker === uniqueIdentifierUUID &&
                      popUpContent !== null ? (
                      <InfoWindow
                        position={{ lat: latitude, lng: longitude }}
                        onCloseClick={() => setActiveMarker(null)}
                      >
                        {popUpContent(marker)}
                      </InfoWindow>
                    ) : null}
                    {/* </Suspense> */}
                  </OverlayView>
                ) : (
                  <Marker
                    key={uniqueIdentifierUUID}
                    position={{ lat: latitude, lng: longitude }}
                    clusterer={clusterer} // Attach the clusterer instance here
                    onClick={() => {
                      if (handleMarkerClick) {
                        handleMarkerClick(marker);
                      } else {
                        setActiveMarker(uniqueIdentifierUUID);
                      }
                    }}
                  >
                    {/* Clicking on a marker sets the active marker as the unique identifier. This lets us know which marker to display */}
                    {activeMarker === uniqueIdentifierUUID &&
                      popUpContent !== null ? (
                      <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                        {popUpContent(marker)}
                      </InfoWindow>
                    ) : null}
                  </Marker>
                );
              })
              : null
          }
        </MarkerClusterer>

        {/* Polylines */}
        {polylines.length > 0
          ? polylines.map((polyline, index) => {
            const { path = [], color = "blue", weight = 5 } = polyline;

            return (
              <Polyline
                key={index}
                onClick={(e) => {
                  setPolylineInfoWindowState({
                    data: polyline,
                    isOpen: true,
                    position: { lat: e.latLng.lat(), lng: e.latLng.lng() },
                  });
                }}
                options={{ strokeColor: color, strokeWeight: weight }}
                path={path}
              ></Polyline>
            );
          })
          : null}

        {/* Additional Map Items */}
        {additionalMapItems ? additionalMapItems : null}

        {/* Drawing Manager */}
        <DrawingManager
          ref={drawingManagerRef}
          options={{
            drawingControl,
            drawingControlOptions: {
              position: window.google.maps.ControlPosition.TOP_CENTER,
              drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
            },
            polygonOptions: {
              fillColor: "#ff0000",
              fillOpacity: 0.3,
              strokeOpacity: 1,
              strokeWeight: 5,
              visible: true,
            },
          }}
          // onPolygonComplete={(polygon) => {
          //   handlePolygonComplete(polygon);
          // }}
          onPolygonComplete={handlePolygonComplete}
        />


        {/* Polygons */}
        {/* {polygons.map((item, index) => {
          const {
            facility = {},
            path = [],
            fillColor = "#ff47d4",
            paths = [],
          } = item;
          const { location = {}, name = "" } = facility;
          const {
            address1 = "",
            city = "",
            state = "",
            postalCode = "",
          } = location;

          const polygonContentOnClick = <div>{name}</div>;

          return (
            <Polygon
              key={index}
              onClick={() =>
                setState((prevState) => ({
                  ...prevState,
                  infoWindowContent: PolygonPopUpContent({ facility }),
                  //conditionally render based on whether path or paths is provided
                  infoWindowLocation: path.length > 0 ? path[0] : paths[0],
                  showInfoWindow: true,
                }))
              }
              visible={true}
              options={{ strokeColor: "purple", fillColor, fillOpacity: 0.1 }}
              paths={path.length > 0 ? path : paths} // Use path or paths based on availability
            />
          );
        })} */}

        {/* Zones */}
        {/* use data from  circlesToRender. has center.lat, center.long, and radius as well as name which i want rendered in popup*/}
        {circlesToRender.map((circle, index) => {
          const { xyz = {}, radius = 100, name = "", zoneId } = circle;
          const { x = 0, y = 0 } = xyz;

          return (
            <Circle
              key={index}
              center={{ lat: x, lng: y }}
              radius={radius}
              options={{
                strokeColor: "green",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: highlightedZone === zoneId ? "red" : "green",
                fillOpacity: 0.35,
              }}
              onClick={() => {
                onCircleClick(zoneId); // <-- pass zoneId back
              }}
            />
          );
        })}

        {/* Facilities Markers */}
        {facilities.map((facility, index) => {
          const { facilityId = "", location = {} } = facility;
          const { latitude = 0, longitude = 0 } = location;

          return (
            <Marker
              key={`facility-${index}`}
              position={{ lat: latitude, lng: longitude }}
              icon={{
                url:
                  "data:image/svg+xml;charset=UTF-8," +
                  encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="blue" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `),
                scaledSize: new window.google.maps.Size(24, 24), // Adjust size as needed
              }}
              onClick={() => {
                setActiveMarker(facilityId);
              }}
              zIndex={1000} // Ensure the marker is on top of other elements
            >
              {/* Clicking on a marker sets the active marker as the unique identifier. This lets us know which marker to display */}
              {activeMarker === facilityId && popUpContent !== null ? (
                <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                  {facilityPopUpContent(facility)}
                </InfoWindow>
              ) : null}
            </Marker>
          );
        })}

        {/* Directions Service */}
        {requestDirections && origin && destination && (
          <DirectionsService
            options={{
              origin,
              destination,
              travelMode,
            }}
            callback={handleDirectionsCallback}
          />
        )}

        {/* Directions Renderer */}
        {directionsResponse && (
          <DirectionsRenderer
            options={{
              directions: directionsResponse,
              preserveViewport: isRouteRendered, // Preserve viewport after the initial render
              draggable: true, // Allow the user to drag the route
            }}
            onLoad={(directionsRenderer) => {
              // Ensure the DirectionsRenderer does not block map interactions
              directionsRenderer.setOptions({
                draggable: true,
                preserveViewport: isRouteRendered,
              });

              // Mark the route as rendered after the initial load
              if (!isRouteRendered) {
                setIsRouteRendered(true);
              }

              // Fit the map to the route bounds
              const bounds =
                directionsRenderer.getDirections().routes[0].bounds;
              mapRef.current.fitBounds(bounds);
            }}
          />
        )}

        {/* Dotted Line for Non-Navigable Path */}
        {dottedLinePath.length > 0 && (
          <Polyline
            path={dottedLinePath}
            options={{
              strokeColor: "#0000FF", // color for the dotted line
              strokeOpacity: 0, // Make the main line fully transparent
              strokeWeight: 2, // Thickness of the line
              icons: [
                {
                  icon: {
                    path: "M 0,-1 0,1", // Dotted pattern
                    strokeOpacity: 0.5, // Opacity of the dots
                    scale: 2, // Size of the dots
                  },
                  offset: "0", // Start position of the pattern
                  repeat: "10px", // Distance between dots
                },
              ],
            }}
          />
        )}
      </GoogleMap>
    </>
  ) : null;
}
